// =============================================================================
// StoreFilter — reusable store-scoped view selector
// =============================================================================
// A single dropdown that narrows a list/report to one store (Connection).
// Used across Orders, Products, Payments and Wallet. The selected value is a
// single `connectionId` convention shared by all pages:
//
//   ''      → All stores (no filter)
//   'none'  → Manual / no store (only when `includeManual`)
//   <cuid>  → a specific store
//
// This convention is intentionally the same one a future GLOBAL store
// switcher would emit, so pages can be promoted without reworking their
// query wiring. Store visibility here is a VIEW convenience, never an access
// boundary — the API always enforces org scope server-side.
//
// Uninstalled/disconnected stores are still listed (so historical data stays
// filterable) but clearly labelled as inactive.
// =============================================================================

import { Select } from '@tkpod/ui';

import { useConnections } from '../hooks/use-connections';

/** Sentinel for the "manual / no store" bucket. Mirrors the API DTO value. */
export const NO_STORE = 'none';

interface StoreFilterProps {
  /** Current value: '' (all), 'none' (manual), or a connection id. */
  value: string;
  /** Called with the new value when the selection changes. */
  onChange: (value: string) => void;
  /**
   * When true, adds a "Manual (no store)" option for rows without a
   * connection (manually created orders / unassigned ledger spend). Pages
   * where a manual bucket is meaningless (e.g. Products) leave this false.
   */
  includeManual?: boolean;
  /** Optional field label (omit for inline/header placement). */
  label?: string;
}

export function StoreFilter({
  value,
  onChange,
  includeManual = false,
  label,
}: StoreFilterProps) {
  const connectionsQuery = useConnections();
  const connections = connectionsQuery.data ?? [];

  const options = [
    { value: '', label: 'All stores' },
    ...(includeManual ? [{ value: NO_STORE, label: 'Manual (no store)' }] : []),
    ...connections.map((c) => ({
      value: c.id,
      // Label uninstalled / disconnected stores so historical filtering is
      // possible without implying the store is still live.
      label: c.status === 'ACTIVE' ? c.name : `${c.name} (inactive)`,
    })),
  ];

  return (
    <Select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      options={options}
      disabled={connectionsQuery.isLoading}
      aria-label="Filter by store"
    />
  );
}
